'use client';

import { useCreateAccount } from '@/hooks/CreateAccount/useCreateAccount';
import { GET_CART_DETAILS_QUERY } from '@/hooks/SignIn/signIn.gql';
import { useSignIn } from '@/hooks/SignIn/useSignIn';
import useIsMobile from '@/hooks/use-is-mobile';
import { hasLengthAtLeast, isEqualToField, isRequired, validateEmail2 } from '@/util/formValidators';
import { ErrorMessage } from '@hookform/error-message';
import clsx from 'clsx';
import Link from 'next/link';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import FormError from './FormError';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Drawer, DrawerContent, DrawerTitle } from './ui/drawer';

interface Props {
  open: boolean;
  setOpen: (nextOpen: boolean) => void;
  setLoginConfirmLoading: (loading: boolean) => void;
  afterLogin?: (loginOk: boolean) => void;
  confirmLoading?: boolean;
}

const LoginDialog = (props: Props) => {
  const { open, setOpen, afterLogin, confirmLoading, setLoginConfirmLoading } = props;

  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const { register, handleSubmit, formState, trigger } = useForm({
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
  });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

  const isMobile = useIsMobile();
  const {
    isSigningIn,
    handleSubmit: handleSubmitSignIn,
    errors: signInErrors,
  } = useSignIn({
    getCartDetailsQuery: GET_CART_DETAILS_QUERY,
  });
  const {
    isSubmitting: isCreatingAccount,
    handleSubmit: handleSubmitCreateAccount,
    errors: createAccountErrors,
  } = useCreateAccount({});

  const isLogin = activeTab === 'login';
  const isRegister = activeTab === 'register';

  const handleSubmitForm = async values => {
    setLoginConfirmLoading(true);

    if (isLogin) {
      const loginOk = await handleSubmitSignIn({
        values: {
          email: values.email,
          password: values.password,
        },
      });
      afterLogin?.(loginOk);
      return;
    }

    if (isRegister) {
      const loginOk = await handleSubmitCreateAccount({
        values: {
          customer: {
            email: values.email,
          },
          password: values.password,
        },
      });
      afterLogin?.(loginOk);
      return;
    }
  };

  const mainContent = (
    <div className='h-screen max-h-[465px] overflow-auto bg-white lg_max-h-[580px] '>
      <button
        type='button'
        className='ml-auto block px-2.5 py-4 lg_px-5 lg_pb-0 lg_pt-5'
        onClick={() => {
          setOpen(false);
        }}
      >
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='18'
          height='18'
          fill='none'
          viewBox='0 0 18 18'
        >
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='1.5'
            d='m16.75 16.75-16-16m16 0-16 16'
          ></path>
        </svg>
      </button>

      <div className='flex items-center px-4 lg_mt-[93px] lg_px-[46px]'>
        <button
          type='button'
          className={clsx(
            'relative font-Shango text-[22px] leading-[27px]',
            isLogin
              ? 'text-80583F after_absolute after_bottom-0 after_left-0 after_h-0.5 after_w-full after_bg-80583F after_content-[""]'
              : 'text-8B857D',
            'flex-1 border-b-1 border-solid border-8B857D pb-2.5',
          )}
          onClick={() => {
            setActiveTab('login');
          }}
        >
          LOGIN
        </button>
        <button
          type='button'
          className={clsx(
            'relative font-Shango text-[22px] leading-[27px]',
            isRegister
              ? 'text-80583F after_absolute after_bottom-0 after_left-0 after_h-0.5 after_w-full after_bg-80583F after_content-[""]'
              : 'text-8B857D',
            'flex-1 border-b-1 border-solid border-8B857D pb-2.5',
          )}
          onClick={() => {
            setActiveTab('register');
          }}
        >
          REGISTER
        </button>
      </div>

      <form className='mx-auto mt-11 px-4 lg_mt-5 lg_max-w-[540px] lg_px-0'>
        <div>
          <div className='text-[14px] leading-5 before_mr-1 before_text-[14px] before_leading-5 before_text-e73635 before_content-["*"]'>
            Email
          </div>
          <input
            {...register('email', { validate: validateEmail2 })}
            className='mt-1.5 h-10 w-full border-1 border-solid border-E7DED5 px-[15px] text-[14px] leading-5'
          />
          <ErrorMessage
            name='email'
            errors={formState.errors}
            render={({ message }) => {
              return (
                <div className='relative'>
                  <span className='absolute text-xs text-e73635'>{message}</span>
                </div>
              );
            }}
          />
        </div>

        <div className='mt-[22px]'>
          <div className={clsx('flex items-center ', isLogin ? 'justify-between' : 'justify-start')}>
            <span className='text-[14px] leading-5 before_mr-1 before_text-[14px] before_leading-5 before_text-e73635 before_content-["*"]'>
              Password
            </span>
            {isLogin ? (
              <Link
                className='text-[12px] leading-4 text-8B857D underline'
                href='/forgot-password'
                onClick={() => {
                  setOpen(false);
                }}
              >
                Forgot Your Password?
              </Link>
            ) : null}
          </div>
          <div className='relative mt-1.5'>
            <input
              {...register('password', {
                validate: (password, values) => {
                  if (isRegister) {
                    return isRequired(password) || hasLengthAtLeast(password, values, 6);
                  }

                  return isRequired(password);
                },
              })}
              type={passwordVisible ? 'text' : 'password'}
              className='h-10 w-full border-1 border-solid border-E7DED5 px-[15px] text-[14px] leading-5'
            />
            <button
              onClick={() => {
                setPasswordVisible(x => !x);
              }}
              className='absolute right-4 top-1/2 -translate-y-1/2'
              type='button'
            >
              {passwordVisible ? eyeIcon : eyeOffIcon}
            </button>
          </div>
          <ErrorMessage
            name='password'
            errors={formState.errors}
            render={({ message }) => {
              return (
                <div className='relative'>
                  <span className='absolute text-xs text-e73635'>{message}</span>
                </div>
              );
            }}
          />
        </div>

        {isRegister ? (
          <div className='mt-[22px]'>
            <div className={clsx('flex items-center ', 'justify-start')}>
              <span className='text-[14px] leading-5 before_mr-1 before_text-[14px] before_leading-5 before_text-e73635 before_content-["*"]'>
                Confirm Password
              </span>
            </div>
            <div className='relative mt-1.5'>
              <input
                {...register('confirmPassword', {
                  validate: (confirmPassword, values) => {
                    return (
                      isRequired(confirmPassword) ||
                      hasLengthAtLeast(confirmPassword, values, 6) ||
                      isEqualToField(confirmPassword, values, 'password')
                    );
                  },
                })}
                type={confirmPasswordVisible ? 'text' : 'password'}
                className='h-10 w-full border-1 border-solid border-E7DED5 px-[15px] text-[14px] leading-5'
              />
              <button
                onClick={() => {
                  setConfirmPasswordVisible(x => !x);
                }}
                className='absolute right-4 top-1/2 -translate-y-1/2'
                type='button'
              >
                {confirmPasswordVisible ? eyeIcon : eyeOffIcon}
              </button>
            </div>
            <ErrorMessage
              name='confirmPassword'
              errors={formState.errors}
              render={({ message }) => {
                return (
                  <div className='relative'>
                    <span className='absolute text-xs text-e73635'>{message}</span>
                  </div>
                );
              }}
            />
          </div>
        ) : null}

        <button
          type='button'
          disabled={isSigningIn}
          onClick={async () => {
            const isFormValid = await trigger();

            if (!isFormValid) return;

            handleSubmit(handleSubmitForm)();
          }}
          className='mt-[30px] flex h-12 w-full items-center justify-center rounded-[3px] border-1 border-solid border-80583F bg-80583F text-center text-[15px] leading-[1.4] text-white lg_hover_enabled_bg-transparent lg_hover_enabled_text-80583F'
        >
          {isLogin ? 'LOGIN' : isRegister ? 'CREATE AN ACCOUNT' : null}
          {isSigningIn || confirmLoading || isCreatingAccount ? '...' : null}
        </button>

        <FormError
          allowErrorMessages
          errors={[...Array.from(signInErrors.values()), ...Array.from(createAccountErrors.values())]}
          scrollOnError={false}
          classes={{ root: 'mt-2.5' }}
        />
      </form>
    </div>
  );

  return isMobile ? (
    <Drawer
      open={open}
      onOpenChange={setOpen}
      direction='bottom'
      handleOnly
    >
      <DrawerContent
        maskClassName={clsx('bg-black/70')}
        className='fixed bottom-0 left-0 z-[400] w-full'
      >
        <DrawerTitle className='hidden'>login</DrawerTitle>
        {mainContent}
      </DrawerContent>
    </Drawer>
  ) : (
    <Dialog
      open={open}
      onOpenChange={setOpen}
    >
      <DialogContent
        showCloseButton={false}
        className='block w-full max-w-[852px] gap-y-0 border-none p-0 outline-0'
      >
        <DialogTitle className='hidden'>login</DialogTitle>
        {mainContent}
      </DialogContent>
    </Dialog>
  );
};

const eyeIcon = (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    fill='none'
    stroke='currentColor'
    strokeLinecap='round'
    strokeLinejoin='round'
    strokeWidth='2'
    viewBox='0 0 24 24'
    className='h-auto w-5'
  >
    <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8' />
    <circle
      cx='12'
      cy='12'
      r='3'
    />
  </svg>
);
const eyeOffIcon = (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    fill='none'
    stroke='currentColor'
    strokeLinecap='round'
    strokeLinejoin='round'
    strokeWidth='2'
    viewBox='0 0 24 24'
    className='h-auto w-5'
  >
    <path d='M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9 9 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22' />
  </svg>
);

export default LoginDialog;
